import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API_HOST } from "../../constants";
import { getCookie } from "../../utils/cookieUtil";
import { useSession } from "../Login/SessionContext";
import Bbs from "./Bbs";
import Comment from "./Comment";
import BbsVoteButton from "./BbsVoteButton";
import LoadingAnime from "../Common/LoadingAnime";
import Button from "../ui/Button";
import { isAdminUser } from "../../utils/userUtils";
import DOMPurify from "dompurify";
import { Helmet } from "react-helmet-async";


function BbsView() {
  const { isLoading, user } = useSession();
  const navigate = useNavigate();
  const { moduleName, document_id } = useParams();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);



  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API_HOST}/api/bbs/${moduleName}/${document_id}/`);
        setPost(data);
      } catch (err) {
        setError(err.response?.data || "Error loading document");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [moduleName, document_id, refresh]);

  const handleDeletePost = async () => {
    if (!window.confirm("삭제 하시겠습니까?")) return;
    try {
      const csrfToken = getCookie("csrftoken");
      await axios.delete(`${API_HOST}/api/bbs/${document_id}/`, {
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        withCredentials: true,
      });
      navigate(`/bbs/${moduleName}`);
    } catch (err) {
      console.error("Error deleting post:", err);
    }
  };

  const handleEditPost = () => navigate(`/bbs/${moduleName}/edit/${document_id}`);

  const handleToggleNotice = async () => {
    try {
      setActionLoading(true);
      const csrfToken = getCookie("csrftoken");
      const response = await axios.patch(
        `${API_HOST}/api/bbs/${document_id}/`,
        { is_notice: !post.is_notice },
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        }
      );
      setPost(response.data);
    } catch (err) {
      alert(err.response?.data?.error || "공지 설정에 실패했습니다.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleHidePost = async () => {
    if (!window.confirm("게시글을 숨기시겠습니까?")) return;
    try {
      setActionLoading(true);
      const csrfToken = getCookie("csrftoken");
      const response = await axios.patch(
        `${API_HOST}/api/bbs/${document_id}/`,
        { is_deleted: true },
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        }
      );
      setPost(response.data);
    } catch (err) {
      alert("게시글 숨김 처리에 실패했습니다.");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading || (isLoading && user)) return <LoadingAnime label="게시글을 불러오는 중..." />;

  if (error) {
    return (
      <div className="glass-card p-6">
        <h4 className="text-lg font-semibold text-white">문제가 발생했습니다</h4>
        <p className="mt-2 text-sm text-ink-200">{error}</p>
      </div>
    );
  }

  return (
    <>
      {post && (
        <Helmet>
          <title>{post.title} - 썰모</title>
          <meta name="description" content={post.title} />
        </Helmet>
      )}
      <div className="space-y-8">
        <article className="glass-card p-4 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <span className="chip">{post.category_name}</span>
              <h1 className="mt-3 font-display text-3xl text-white">{post.title}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ink-200">
                <span className="pill">작성자 {post.author_nickname}</span>
                <span className="pill">댓글 {post.comments.length}</span>
                <span className="pill">조회 {post.view_count}</span>
                <span className="pill">추천 {post.like_count}</span>
                <span className="pill">비추천 {post.dislike_count}</span>
                <span className="pill">
                  {new Date(post.created_at).toLocaleDateString("en-CA")} {" "}
                  {new Date(post.created_at).toLocaleTimeString("en-US", { hour12: false })}
                </span>
              </div>
              {post.tags && post.tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span key={tag.id} className="chip">
                      #{tag.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {(user?.id === post.author || isAdminUser(user)) && (
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={handleEditPost}>
                  수정
                </Button>
                <Button variant="danger" onClick={handleDeletePost}>
                  삭제
                </Button>
              </div>
            )}
          </div>

          <div
            className="mt-6 text-sm leading-[1.6] text-white"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(post.content),
            }}
          />

          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            <BbsVoteButton document_id={post.document_id} likeCount={post.like_count} dislikeCount={post.dislike_count} />
          </div>
        </article>

        {isAdminUser(user) && (
          <section className="glass-card p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-white">관리자 퀵 액션</h2>
            <p className="mt-1 text-xs text-ink-200">현재 게시글을 즉시 관리합니다.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="danger" onClick={handleDeletePost}>
                게시글 삭제
              </Button>
              <Button variant="secondary" onClick={handleToggleNotice} disabled={actionLoading}>
                {post.is_notice ? "공지 해제" : "공지 고정"}
              </Button>
              <Button variant="secondary" onClick={handleHidePost} disabled={actionLoading || post.is_deleted}>
                {post.is_deleted ? "숨김 처리됨" : "게시글 숨김"}
              </Button>
            </div>
          </section>
        )}

        <Comment comments={post.comments} document_id={document_id} onSuccess={() => setRefresh(!refresh)} />

        <section className="glass-card p-4 sm:p-6">
          {/* <h3 className="text-lg font-semibold text-white">다른 이야기</h3> */}
          {/* <p className="text-xs text-ink-200">같은 게시판의 최신 글을 확인하세요.</p> */}
          <div className="mt-4">
            <Bbs
              showBbsModule={false}
              compact
              activeDocumentId={post.document_id}
              document_id={document_id}
              key={`${moduleName}-${document_id}`}
            />
          </div>
        </section>
      </div>
    </>
    
  );
}

export default BbsView;
